/**
 * sessionStorage persistence for the add-a-machine page's pending link
 * (forge#3520).
 *
 * Minting a link and watching for the machine are two phases of one visit, but
 * the user routinely navigates away between them. Persisting the minted link
 * keyed to this tab means a return trip restores the pending banner (with its
 * Revoke button) instead of minting a second, orphaned credential. The stored
 * record carries the pairing URL because the credential in it exists only in
 * the hash fragment and cannot be re-read from the server, and the link's
 * unique label because the linked poll attributes sessions with it; the record
 * dies with the tab, and every exit path (linked, revoked, expired) clears it.
 *
 * Every access is best-effort: a storage failure (private mode, quota, denied
 * access) degrades to "no restore" and a silent save, never breaks pairing.
 */

export interface PendingPairingLink {
  readonly linkId: string;
  readonly pairingUrl: string;
  readonly expiresAt: string;
  /** The unique label this link was minted with; the server copies it onto
   * the session the link creates, so a restored link stays attributable. */
  readonly label: string;
  /** Session ids observed before the credential was exposed; the linked
   * signal is a session whose id is not in this set. */
  readonly baselineSessionIds: readonly string[];
}

const PENDING_LINK_STORAGE_KEY = "forge.pairMachine.pendingLink";

export function loadPendingPairingLink(storage: Storage): PendingPairingLink | null {
  try {
    const raw = storage.getItem(PENDING_LINK_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<PendingPairingLink> | null;
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      typeof parsed["linkId"] !== "string" ||
      typeof parsed["pairingUrl"] !== "string" ||
      typeof parsed["expiresAt"] !== "string" ||
      typeof parsed["label"] !== "string" ||
      !Array.isArray(parsed["baselineSessionIds"])
    ) {
      return null;
    }
    const ids = parsed["baselineSessionIds"].filter((id): id is string => typeof id === "string");
    return {
      linkId: parsed["linkId"],
      pairingUrl: parsed["pairingUrl"],
      expiresAt: parsed["expiresAt"],
      label: parsed["label"],
      baselineSessionIds: ids,
    };
  } catch {
    // Unreadable storage is the same as no stored link.
    return null;
  }
}

export function savePendingPairingLink(storage: Storage, link: PendingPairingLink): void {
  try {
    storage.setItem(PENDING_LINK_STORAGE_KEY, JSON.stringify(link));
  } catch {
    // Restore is a convenience; a storage failure costs it, not pairing.
  }
}

export function clearPendingPairingLink(storage: Storage): void {
  try {
    storage.removeItem(PENDING_LINK_STORAGE_KEY);
  } catch {
    // Nothing to do: the record dies with the tab regardless.
  }
}

export function isPendingPairingLinkExpired(link: PendingPairingLink, now: number): boolean {
  return Date.parse(link.expiresAt) <= now;
}
